import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import ConfirmDialog from "./ConfirmDialog";
import { TbPhoto } from "react-icons/tb";

const MAX_SIZE = 2 * 1024 * 1024;

const LIST_UNIT = ["mm", "inch"];
const LIST_RADIUS = [
  "-",
  "BO",
  "R0.5",
  "R1",
  "R2",
  "R2.5",
  "R3",
  "R3.5",
  "R4",
  "R4.5",
  "R5",
  "R6",
];
const LIST_MODEL = ["DIAMETER", "OVAL", "INNER", "ARROW", "SPECIAL", "STAR"];

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400";
const sel =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white";

function NewDiecut() {
  const [viewType, setViewType] = useState("0");
  const [unit, setUnit] = useState("");
  const [radius, setRadius] = useState("");
  const [model, setModel] = useState("");
  const [diecutLength, setDiecutLength] = useState("");
  const [diecutWidth, setDiecutWidth] = useState("");
  const [diecutUps, setDiecutUps] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetForm = () => {
    setUnit("");
    setRadius("");
    setModel("");
    setDiecutLength("");
    setDiecutWidth("");
    setDiecutUps("");
    clearImage();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("Image must be under 2MB.");
      e.target.value = "";
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleViewTypeChange = (val) => {
    setViewType(val);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (viewType === "0") {
      if (!unit || !radius || !diecutLength || !diecutWidth || !diecutUps) {
        toast.warning("Please fill in all fields.");
        return;
      }
    } else {
      if (!model || !unit || !diecutLength || !diecutUps) {
        toast.warning("Please fill in all required fields.");
        return;
      }
    }
    setShowConfirm(true);
  };

  const executeInsert = async () => {
    setIsLoading(true);
    try {
      const fd = new FormData();
      if (viewType === "0") {
        fd.append("die_p", diecutLength);
        fd.append("die_l", diecutWidth);
        fd.append("die_unit", unit);
        fd.append("die_r", radius);
        fd.append("die_ups", diecutUps);
      } else {
        fd.append("spec_model", model);
        fd.append("spec_p", diecutLength);
        fd.append("spec_l", diecutWidth);
        fd.append("spec_unit", unit);
        fd.append("spec_ups", diecutUps);
      }
      if (imageFile) fd.append("image", imageFile);

      const endpoint = viewType === "0" ? "normal" : "special";
      await axios.post(
        `https://eps.elkomprintama.com/post/api/diecut/${endpoint}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        },
      );
      toast.success("Diecut added successfully!");
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to insert diecut.");
    } finally {
      setIsLoading(false);
    }
  };

  const isDiameter = model === "DIAMETER";

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {/* Header */}
      <div className="w-full rounded-lg bg-gray-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Add Diecut
        </label>
      </div>

      {/* Form card */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Type toggle */}
            <div>
              <label className={lbl}>Diecut Type</label>
              <div className="flex gap-3">
                {[
                  ["0", "Normal Diecut"],
                  ["1", "Special Diecut"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => handleViewTypeChange(val)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition ${
                      viewType === val
                        ? "bg-gray-700 text-white"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Normal fields */}
            {viewType === "0" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Length</label>
                    <input
                      className={inp}
                      name="diecutlength"
                      type="text"
                      placeholder="e.g. 100"
                      value={diecutLength}
                      onChange={(e) => setDiecutLength(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={lbl}>Width</label>
                    <input
                      className={inp}
                      name="diecutwidth"
                      type="text"
                      placeholder="e.g. 50"
                      value={diecutWidth}
                      onChange={(e) => setDiecutWidth(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={lbl}>Unit</label>
                    <select
                      className={sel}
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                    >
                      <option value="">Select</option>
                      {LIST_UNIT.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Radius</label>
                    <select
                      className={sel}
                      value={radius}
                      onChange={(e) => setRadius(e.target.value)}
                    >
                      <option value="">Select</option>
                      {LIST_RADIUS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Ups</label>
                    <input
                      className={inp}
                      name="diecutups"
                      type="text"
                      placeholder="e.g. 2"
                      value={diecutUps}
                      onChange={(e) => setDiecutUps(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Special fields */}
            {viewType === "1" && (
              <>
                <div>
                  <label className={lbl}>Model</label>
                  <select
                    className={sel}
                    value={model}
                    onChange={(e) => {
                      const v = e.target.value;
                      setModel(v);
                      if (v === "DIAMETER") {
                        setDiecutWidth("0");
                      } else {
                        setDiecutLength("");
                        setDiecutWidth("");
                        setDiecutUps("");
                      }
                    }}
                  >
                    <option value="">Select model</option>
                    {LIST_MODEL.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>
                      {isDiameter ? "Diameter" : "Length"}
                    </label>
                    <input
                      className={inp}
                      name="diecutlength"
                      type="text"
                      placeholder="e.g. 100"
                      value={diecutLength}
                      onChange={(e) => setDiecutLength(e.target.value)}
                    />
                  </div>
                  {!isDiameter && (
                    <div>
                      <label className={lbl}>Width</label>
                      <input
                        className={inp}
                        name="diecutwidth"
                        type="text"
                        placeholder="e.g. 50"
                        value={diecutWidth}
                        onChange={(e) => setDiecutWidth(e.target.value)}
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Unit</label>
                    <select
                      className={sel}
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                    >
                      <option value="">Select</option>
                      {LIST_UNIT.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Ups</label>
                    <input
                      className={inp}
                      name="diecutups"
                      type="text"
                      placeholder="e.g. 2"
                      value={diecutUps}
                      onChange={(e) => setDiecutUps(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Image upload */}
            <div>
              <label className={lbl}>
                Diecut Image{" "}
                <span className="normal-case font-normal text-gray-400">
                  (optional, max 2MB)
                </span>
              </label>
              <div
                className="w-full border-2 border-dashed border-gray-300 rounded-lg px-3 py-5 text-center cursor-pointer hover:border-gray-400 transition"
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-40 mx-auto object-contain rounded"
                  />
                ) : (
                  <>
                    <TbPhoto className="mx-auto mb-1 text-gray-300" size={28} />
                    <p className="text-xs text-gray-400">
                      Click to browse an image
                    </p>
                    <p className="text-xs text-gray-300 mt-0.5">
                      PNG, JPG up to 2MB
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              {imageFile && (
                <div className="flex items-center justify-between mt-2 px-1">
                  <span className="text-xs text-gray-500 truncate">
                    {imageFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={clearImage}
                    className="text-xs text-red-500 hover:text-red-700 font-semibold ml-2"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl text-sm font-bold text-white bg-gray-700 hover:bg-gray-800 disabled:opacity-50 transition"
            >
              {isLoading ? "Inserting…" : "Insert Diecut"}
            </button>
          </form>
        </div>
      </div>

      {showConfirm && (
        <ConfirmDialog
          message="Insert this diecut record?"
          onConfirm={() => {
            setShowConfirm(false);
            executeInsert();
          }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}

export default NewDiecut;
